/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

// ---------------------------------------------------------------------------
// 1OS Universal Login Standard — shared shell component.
//
//   <OneOSLoginShell
//     engineCode="SAL"
//     engineFull="Sales Capture"
//     onSubmit={async ({ email, password }) => { ... }}
//     error={string}
//     submitting={bool}
//   />
//
// This component owns ALL visual decisions for 1OS login surfaces. Consumers
// only pass engine identity + auth-handler callbacks. Anything else (colors,
// fonts, motion, copy, layout) is frozen by the design system.
//
// See /app/memory/ONEOS_LOGIN_STANDARD.md for the full spec and rationale.
// Do not override, extend, or fork this component per-product.
// ---------------------------------------------------------------------------

const ONE_LOGO =
  'https://customer-assets.emergentagent.com/job_canonical-sales/artifacts/htsmmsb8_1OS%20LOGO%20transparent.png';

// Central registry — add new engines here, never in consumer code.
export const ENGINES = {
  inventory: { engineCode: 'INV',  engineFull: 'Inventory'       },
  sales:     { engineCode: 'SAL',  engineFull: 'Sales Capture'   },
  accounts:  { engineCode: 'ACC',  engineFull: 'Accounts'        },
  expense:   { engineCode: 'EXP',  engineFull: 'Expense Capture' },
  people:    { engineCode: 'HR',   engineFull: 'People'          },
  control:   { engineCode: 'CTRL', engineFull: 'Control Room'    },
};

// System-truth tone. Never feature talk, never promise talk.
const OUTCOMES = [
  'Everything is already connected.',
  'Nothing here needs guessing.',
  'Your system already knows.',
  'This is your operational ground truth.',
  'The work is already visible.',
  'Already watching. Already aligned.',
  'Every signal, one surface.',
  'No silos. No fog. Just the numbers.',
];

const VISIBLE_MS_DESKTOP = 2500;
const VISIBLE_MS_MOBILE  = 3500;
const FADE_MS = 300;

// ---------------------------------------------------------------------------
// Rotating index — single source of truth. Both desktop and mobile surfaces
// share this so they never drift. Picks dwell based on a simple resize-aware
// isMobile heuristic.
// ---------------------------------------------------------------------------
const useRotatingIndex = () => {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const timersRef = useRef([]);

  useEffect(() => {
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return () => {};

    let stopped = false;
    const isMobileNow = () => window.matchMedia?.('(max-width: 1023px)').matches;

    const cycle = () => {
      if (stopped) return;
      const dwell = isMobileNow() ? VISIBLE_MS_MOBILE : VISIBLE_MS_DESKTOP;
      const out = setTimeout(() => {
        setVisible(false);
        const swap = setTimeout(() => {
          setIndex((i) => (i + 1) % OUTCOMES.length);
          setVisible(true);
          cycle();
        }, FADE_MS);
        timersRef.current.push(swap);
      }, dwell);
      timersRef.current.push(out);
    };
    cycle();

    const onVis = () => {
      if (document.hidden) {
        stopped = true;
        timersRef.current.forEach(clearTimeout);
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stopped = true;
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  return { index, visible };
};

// ---------------------------------------------------------------------------
// Ambient surfaces (desktop absolute / mobile inline).
// ---------------------------------------------------------------------------
const DesktopAmbient = ({ index, visible }) => (
  <div
    aria-hidden="true"
    data-testid="oneos-ambient-desktop"
    className="pointer-events-none absolute inset-0 hidden lg:flex items-center justify-end pr-[6vw] xl:pr-[10vw]"
    style={{ zIndex: 2 }}
  >
    <div
      className="max-w-[46rem] text-right transition-all ease-[cubic-bezier(0.22,0.61,0.36,1)]"
      style={{
        opacity: visible ? 0.58 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(-6px)',
        transitionDuration: `${FADE_MS}ms`,
        transitionProperty: 'opacity, transform',
      }}
    >
      <p
        className="font-['Outfit'] font-light tracking-tight leading-[1.08] text-transparent bg-clip-text bg-gradient-to-br from-white via-white/80 to-white/25"
        style={{ fontSize: 'clamp(1.8rem, 4.2vw, 4rem)' }}
      >
        {OUTCOMES[index]}
      </p>
    </div>
  </div>
);

const MobileInlineMessage = ({ index, visible }) => (
  <div
    data-testid="oneos-ambient-mobile"
    className="lg:hidden w-full max-w-[420px] mx-auto text-center"
  >
    <p
      className="font-['Outfit'] font-normal tracking-tight leading-[1.2] text-white/80 text-[1.2rem] sm:text-[1.4rem] transition-opacity"
      style={{
        opacity: visible ? 1 : 0,
        transitionDuration: `${FADE_MS}ms`,
      }}
    >
      {OUTCOMES[index]}
    </p>
  </div>
);

// ---------------------------------------------------------------------------
// Card — the floating glass surface. Rendered once, used by both layouts.
// ---------------------------------------------------------------------------
const LoginCard = ({
  engineFull,
  form, setForm,
  submitting, error,
  onSubmit,
  showBrandHeader,
}) => (
  <div
    data-testid="oneos-login-card"
    className="w-full sm:max-w-[380px] lg:max-w-[336px]
               rounded-2xl border border-white/[0.05]
               bg-[#0A0A0B]/85 backdrop-blur-xl
               shadow-[0_40px_100px_-20px_rgba(0,0,0,0.85)]
               p-6 sm:p-7 lg:p-8"
  >
    {showBrandHeader && (
      <div className="flex flex-col items-center gap-0.5 mb-8">
        <img
          src={ONE_LOGO}
          alt="1OS"
          className="h-28 w-auto select-none"
          draggable="false"
        />
        <span className="text-[12px] uppercase tracking-[0.22em] text-zinc-500 font-medium">
          {engineFull}
        </span>
      </div>
    )}

    <h1 className="text-[1.5rem] lg:text-[1.55rem] leading-[1.1] font-medium font-['Outfit'] tracking-tight mb-1.5">
      Welcome back.
    </h1>
    <p className="text-[13px] text-zinc-500 leading-relaxed mb-6">
      Sign in to continue to the operating layer.
    </p>

    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (submitting) return;
        onSubmit?.({ email: form.email, password: form.password });
      }}
      className="space-y-4"
      noValidate
    >
      <div className="space-y-2">
        <Label htmlFor="oneos-email" className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-medium">
          Email
        </Label>
        <Input
          id="oneos-email"
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="you@company.com"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          disabled={submitting}
          data-testid="login-email-input"
          className="bg-[#111113] border-white/10 h-11 lg:h-10 text-[15px] lg:text-[14px] focus-visible:ring-1 focus-visible:ring-[#F5C518]/60 focus-visible:border-[#F5C518]/40 transition-colors"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="oneos-password" className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-medium">
          Password
        </Label>
        <Input
          id="oneos-password"
          type="password"
          autoComplete="current-password"
          placeholder="••••••••"
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          disabled={submitting}
          data-testid="login-password-input"
          className="bg-[#111113] border-white/10 h-11 lg:h-10 text-[15px] lg:text-[14px] focus-visible:ring-1 focus-visible:ring-[#F5C518]/60 focus-visible:border-[#F5C518]/40 transition-colors"
          required
        />
      </div>

      <div
        className="min-h-[18px] transition-opacity duration-200"
        style={{ opacity: error ? 1 : 0 }}
        role="alert"
        aria-live="polite"
        data-testid="login-error"
      >
        {error && <p className="text-[12px] text-red-400/90">{error}</p>}
      </div>

      <Button
        type="submit"
        disabled={submitting}
        data-testid="login-submit-btn"
        className="w-full h-11 lg:h-10 bg-[#F5C518] hover:bg-[#FFD33A] text-black font-medium text-[14px] lg:text-[13px] tracking-[0.01em] rounded-md transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {submitting ? (
          <span className="inline-flex items-center gap-2">
            <span className="h-3 w-3 rounded-full border-2 border-black/30 border-t-black animate-spin" />
            Authenticating
          </span>
        ) : (
          'Continue'
        )}
      </Button>
    </form>
  </div>
);

// ---------------------------------------------------------------------------
// The shell.
// ---------------------------------------------------------------------------
const OneOSLoginShell = ({
  engineCode,
  engineFull,
  onSubmit,
  error = '',
  submitting = false,
}) => {
  const [form, setForm] = useState({ email: '', password: '' });
  const { index, visible } = useRotatingIndex();

  // Hide the platform badge strictly on this page. Restored on unmount.
  useEffect(() => {
    const badge = document.getElementById('emergent-badge');
    if (!badge) return undefined;
    const prev = badge.getAttribute('style');
    badge.style.setProperty('display', 'none', 'important');
    return () => {
      if (prev === null) badge.removeAttribute('style');
      else badge.setAttribute('style', prev);
    };
  }, []);

  const chipLabel = engineCode
    ? `${engineCode} · CANONICAL API LIVE`
    : 'CANONICAL API LIVE';

  return (
    <div
      data-testid="oneos-login-shell"
      data-engine-code={engineCode}
      className="relative min-h-screen w-full overflow-hidden bg-[#08080A] text-white"
    >
      {/* ----------------------------------------------------------------- */}
      {/* Unified canvas. Barely perceptible amber, grid fades to edges.    */}
      {/* ----------------------------------------------------------------- */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        {/* Base vignette */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_30%,rgba(30,32,38,0.5),transparent_60%)]" />

        {/* Amber — moved further off-canvas + tighter blur so the colour
            whispers rather than announces */}
        <div className="absolute -top-60 -right-60 h-[480px] w-[480px] lg:h-[720px] lg:w-[720px] rounded-full bg-[#F5C518]/[0.08] lg:bg-[#F5C518]/[0.10] blur-[160px] lg:blur-[180px]" />

        {/* Grid — far subtler, heavier radial fade */}
        <div
          className="absolute inset-0 opacity-[0.018] lg:opacity-[0.022]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.8) 1px, transparent 1px)',
            backgroundSize: '72px 72px',
            maskImage: 'radial-gradient(ellipse at 50% 40%, rgba(0,0,0,1) 18%, rgba(0,0,0,0) 70%)',
            WebkitMaskImage: 'radial-gradient(ellipse at 50% 40%, rgba(0,0,0,1) 18%, rgba(0,0,0,0) 70%)',
          }}
        />

        {/* Noise — desktop only, to kill banding */}
        <div
          className="hidden lg:block absolute inset-0 opacity-[0.022] mix-blend-overlay"
          style={{
            backgroundImage:
              'url("data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22200%22 height=%22200%22><filter id=%22n%22><feTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22 numOctaves=%222%22/></filter><rect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22/></svg>")',
          }}
        />

        {/* Engine mark — massive, near-transparent wordmark. Desktop only.
            Architectural signage, not content. Prop-driven from engineCode
            so Sales renders "SAL", Inventory renders "INV", etc. — the
            universal login standard is honoured across every engine by
            construction. */}
        <div
          aria-hidden="true"
          data-testid="oneos-engine-mark"
          className="ambient-engine-mark hidden lg:flex absolute select-none pointer-events-none items-center justify-end"
          style={{
            top: '50%',
            right: '1vw',
            transform: 'translateY(-50%)',
            zIndex: 1,
            fontFamily: "'Outfit', sans-serif",
            fontWeight: 500,
            fontSize: 'clamp(36rem, 105vh, 70rem)',
            lineHeight: 0.78,
            letterSpacing: '-0.07em',
            color: 'rgba(245, 197, 24, 0.04)',
            whiteSpace: 'nowrap',
          }}
        >
          {engineCode}
        </div>
      </div>

      <DesktopAmbient index={index} visible={visible} />

      {/* ----------------------------------------------------------------- */}
      {/* DESKTOP                                                            */}
      {/* ----------------------------------------------------------------- */}
      <div className="hidden lg:flex relative z-10 flex-col min-h-screen">
        <div className="px-10 pt-10 flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-zinc-500 font-medium">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400/80 shadow-[0_0_12px_rgba(16,185,129,0.6)]" />
          {chipLabel}
        </div>
        <div className="flex-1 flex items-center pl-[9vw] xl:pl-[11vw] pr-6">
          <LoginCard
            engineFull={engineFull}
            form={form} setForm={setForm}
            submitting={submitting} error={error}
            onSubmit={onSubmit}
            showBrandHeader
          />
        </div>
        {/* Minimal neutral footer — communicates nothing important */}
        <div className="px-10 pb-8 text-[10px] uppercase tracking-[0.22em] text-zinc-700 font-medium">
          1OS
        </div>
      </div>

      {/* ----------------------------------------------------------------- */}
      {/* MOBILE / TABLET                                                    */}
      {/* ----------------------------------------------------------------- */}
      <div className="lg:hidden relative z-10 min-h-screen flex flex-col px-5 pb-6">
        {/* Logo stack — top */}
        <div className="pt-10 pb-6 flex flex-col items-center gap-0.5">
          <img src={ONE_LOGO} alt="1OS" className="h-28 w-auto select-none" draggable="false" />
          <span className="text-[12px] uppercase tracking-[0.22em] text-zinc-500 font-medium">
            {engineFull}
          </span>
        </div>

        {/* Outcome line */}
        <div className="pb-8">
          <MobileInlineMessage index={index} visible={visible} />
        </div>

        {/* Card */}
        <div className="flex-1 flex items-start sm:items-center justify-center">
          <LoginCard
            engineFull={engineFull}
            form={form} setForm={setForm}
            submitting={submitting} error={error}
            onSubmit={onSubmit}
            showBrandHeader={false}
          />
        </div>

        {/* Ultra-muted footer */}
        <div className="pt-6 flex items-center justify-center text-[10px] uppercase tracking-[0.22em] text-zinc-700 font-medium">
          1OS
        </div>
      </div>
    </div>
  );
};

export default OneOSLoginShell;
